import { PublicSiteShell } from "@/components/PublicSiteShell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GeoConfigSessionRefresh } from "@/components/GeoConfigSessionRefresh";

import { LanguageProvider } from "@/components/LanguageProvider";
import { LanguageUrlSync } from "@/components/LanguageUrlSync";
import { RegionProvider } from "@/components/RegionProvider";
import { SkipLink } from "@/components/SkipLink";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { PostHogProvider } from "@/components/analytics/PostHogProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CurrentTemplateProvider } from "@/contexts/CurrentTemplateContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useLayoutEffect, useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { BannedGate } from "@/components/auth/BannedGate";
import { DevilThemeEffect } from "@/components/auth/DevilThemeEffect";
import { ClerkProvider } from "@clerk/clerk-react";
import { ClerkGateContext, isClerkDeferredPath } from "@/lib/clerkGate";
import { esES, enUS } from "@clerk/localizations";
import { dark } from "@clerk/themes";
import Argentina from "./pages/Argentina";
import InvitacionExclusivaArgentina from "./pages/InvitacionExclusivaArgentina";
import InvitacionArSuccess from "./pages/InvitacionArSuccess";
import InvitacionArError from "./pages/InvitacionArError";
import InvitacionArPending from "./pages/InvitacionArPending";
import PremiumInvite from "./pages/PremiumInvite";
import PremiumInviteSuccess from "./pages/PremiumInviteSuccess";
import MembresiaPremiumVip from "./pages/MembresiaPremiumVip";
import TestNewTemplate from "./pages/TestNewTemplate";
import TestPremiumArgentina from "./pages/TestPremiumArgentina";
import TestPremiumInternacional from "./pages/TestPremiumInternacional";
import {
  AccessControlAdmin,
  ActiveMembersAdmin,
  AdminAuditLog,
  AdminDashboard,
  AnalyticsDashboard,
  AudiovisualAdmin,
  BaseTemplateAdmin,
  BusinessPulse,
  ConfigHealthAdmin,
  ConversionHealthAdmin,
  CountryTemplateAdmin,
  DualCtaHomeAdmin,
  DualCtaVideoTemplatesAdmin,
  VisitorExperienceAdmin,
  VisitorExperienceFunnelsAdmin,
  MercadoPagoPlansAdmin,
  EmailBroadcast,
  FinanzasPersonalesAdmin,
  FreeMemberApprovals,
  GeneradorInvitaciones,
  GeoTrafficDashboard,
  InvestorKpiAdmin,
  IpLocateHealthAdmin,
  MemberLifecycleRecovery,
  MembershipApprovals,
  MuxAnalyticsAdmin,
  MuxConversionAdmin,
  NotificationsHealth,
  PhotoAnalyticsAdmin,
  PortfolioAdmin,
  MediaKitAdmin,
  ComunidadAdmin,
  PremiumInviteAdmin,
  ReelsAdmin,
  ReelsUploadPage,
  ReelsFeedPage,
  ReelsShareLinksPage,
  ReelsSecretSharePage,
  RecoveryAdmin,
  ArgentinaWinCampaignAdmin,
  RevenueAdmin,
  StripeDisputesAdmin,
  StripePaymentsConfigAdmin,
  SorprendemeConfigAdmin,
  WebTemplatesAnalyticsAdmin,
  WhatsAppBroadcastAdmin,
  WishlistAdmin,
  WorkerFlagsAdmin,
} from "./pages/admin/lazyAdminPages";
import Artista from "./pages/Artista";
import Atleta from "./pages/Atleta";
import BeautyExpert from "./pages/BeautyExpert";
import Belleza from "./pages/Belleza";
import Biografia from "./pages/Biografia";
import BuenosAires from "./pages/BuenosAires";
import Contacto from "./pages/Contacto";
import LatinaInfluencer from "./pages/LatinaInfluencer";
import HispanicInfluencer from "./pages/HispanicInfluencer";
// LatinInfluencer → 301 /latina-influencer (cannibalization)
import ModellaArgentina from "./pages/ModellaArgentina";
import Italia from "./pages/Italia";
import London from "./pages/London";
import Usa from "./pages/Usa";
import Miami from "./pages/Miami";
import NewYork from "./pages/NewYork";
import LosAngeles from "./pages/LosAngeles";
import Barcelona from "./pages/Barcelona";
import Bogota from "./pages/Bogota";
import Chile from "./pages/Chile";
import CiudadDeMexico from "./pages/CiudadDeMexico";
import Colombia from "./pages/Colombia";
import Espana from "./pages/Espana";
import Guadalajara from "./pages/Guadalajara";
import Madrid from "./pages/Madrid";
import Medellin from "./pages/Medellin";
import Cali from "./pages/Cali";
import Cartagena from "./pages/Cartagena";
import Mexico from "./pages/Mexico";
import Santiago from "./pages/Santiago";
import Contenido from "./pages/Contenido";
import ContentCreator from "./pages/ContentCreator";
import CreadoraContenido from "./pages/CreadoraContenido";
import Creative from "./pages/Creative";
import Deportista from "./pages/Deportista";
import Desnuda from "./pages/Desnuda";
// DigitalCreator → 301 /content-creator (cannibalization)
import Entrenamiento from "./pages/Entrenamiento";
import Erome from "./pages/Erome";
import Exclusivo from "./pages/Exclusivo";
import ExclusivoArSuccess from "./pages/ExclusivoArSuccess";
import Fashion from "./pages/Fashion";
import FullscreenExclusiveAr from "./pages/FullscreenExclusiveAr";
import Filtrado from "./pages/Filtrados";
import Filtro from "./pages/Filtro";
import Fitness from "./pages/Fitness";
import Fotos from "./pages/Fotos";
import FotosDesnuda from "./pages/FotosDesnuda";
import FotosGratis from "./pages/FotosGratis";
import Galeria from "./pages/Galería";
import Gracias from "./pages/Gracias";
import GraciasPorTuCompra from "./pages/GraciasPorTuCompra";
import GraciasPorTuCompraLatam from "./pages/GraciasPorTuCompraLatam";
import Gratis from "./pages/Gratis";
import Imagenes from "./pages/Imagenes";
import Index from "./pages/Index";
import IndexMinimal from "./pages/IndexMinimal";
import Influencer from "./pages/Influencer";
import Instagram from "./pages/Instagram";
import Invitacion from "./pages/Invitacion";
import InvitacionGrupo from "./pages/InvitacionGrupo";
import InviteMembership from "./pages/InviteMembership";
import Invite from "./pages/Invite";
import InviteGenerator from "./pages/InviteGenerator";
import Leaked from "./pages/Leaked";
import Lifestyle from "./pages/Lifestyle";
import MembresiaTelegram from "./pages/MembresiaTelegram";
import Modelo from "./pages/Modelo";
import NotFound from "./pages/NotFound";
import Nude from "./pages/Nude";
import Nutricion from "./pages/Nutricion";
import Onlyfans from "./pages/Onlyfans";
import Pack from "./pages/Pack";
import Personalidad from "./pages/Personalidad";
import Photography from "./pages/Photography";
import Pornhub from "./pages/Pornhub";
import Premium from "./pages/Premium";
import Privado from "./pages/Privado";
import Recetas from "./pages/Recetas";
import Reels from "./pages/Reels";
import ReelInviteViewer from "./pages/ReelInviteViewer";
import Pack1 from "./pages/Pack1";
import Pack2 from "./pages/Pack2";
import Pack3 from "./pages/Pack3";
// import RegaloLucas from "./pages/RegaloLucas"; // oculto mientras se trabaja
import GiftProduct from "./pages/GiftProduct";
import ProductPageSimple from "./pages/ProductPageSimple";
import DirectCheckout from "./pages/DirectCheckout";
import ProductReel from "./pages/ProductReel";
import ProductReelMockTest from "./pages/ProductReelMockTest";
import ProductPageMockUsd from "./pages/ProductPageMockUsd";
import ProductPageMockArs from "./pages/ProductPageMockArs";
import Rutina from "./pages/Rutina";
import Skincare from "./pages/Skincare";
import SobreMi from "./pages/SobreMi";
import SocialMedia from "./pages/SocialMedia";
import TestMuxCountry from "./pages/TestMuxCountry";
import MuxVideoCountryTest from "./components/dev/MuxVideoCountryTest";
import TestCheckout from "./pages/TestCheckout";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutArChannelInvite from "./pages/CheckoutArChannelInvite";
import CheckoutChannelInviteRouter from "./pages/CheckoutChannelInviteRouter";
import PagarPage from "./pages/PagarPage";
import ChannelInviteLanding from "./pages/ChannelInviteLanding";
import DualCTAVideoLanding from "./pages/DualCTAVideoLanding";
import FullscreenExclusive from "./pages/FullscreenExclusive";
import FullscreenSorpresa from "./pages/FullscreenSorpresa";
import RegaloCumple from "./pages/RegaloCumple";
import MembershipRecoveryVideo from "./pages/MembershipRecoveryVideo";
import ThankYouALot from "./pages/ThankYouALot";
import VipCustomPayment from "./pages/VipCustomPayment";
import TestExclusivo from "./pages/TestExclusivo";
import WhatsAppBroadcast from "./pages/WhatsAppBroadcast";
import TestPage from "./pages/TestPage";
import { TestRunner } from "./pages/TestRunner";
import ProtectedTestRoute from "./components/ProtectedTestRoute";
import StripeValidation from "./pages/StripeValidation";
import ThankYou from "./pages/ThankYou";
import TikTok from "./pages/TikTok";
import Tiktoker from "./pages/Tiktoker";
import TravelInfluencer from "./pages/TravelInfluencer";
import Viajes from "./pages/Viajes";
import Video from "./pages/Video";
import Videos from "./pages/Videos";
import VideosDesnuda from "./pages/VideosDesnuda";
import VideosGratis from "./pages/VideosGratis";
import Wellness from "./pages/Wellness";
import Xvideos from "./pages/Xvideos";
import YouTube from "./pages/YouTube";
import TerminosYCondiciones from "./pages/TerminosYCondiciones";
import DarDeBaja from "./pages/DarDeBaja";
import Portfolio from "./pages/Portfolio";

import { MediaKitPage } from "@/features/media-kit";

import Registro from "./pages/Registro";
import CompleteRegistration from "./pages/CompleteRegistration";
import PendingApproval from "./pages/PendingApproval";
import MiAcceso from "./pages/MiAcceso";
import UserPortal from "./pages/UserPortal";
import MembershipAccessPortal from "./pages/MembershipAccessPortal";
import { MEMBERSHIP_ACCESS_PORTAL_PATH, MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { ProtectedAdminRoute } from "./components/ProtectedAdminRoute";
import {
  getClerkPublishableKey,
  isLikelyClerkPublishableKey,
} from "@/lib/clerkPublishableKey";
import { BRAND_AUTH_LOGO_URL } from "@/lib/brandConfig";
import { isAdminShellPath } from "@/lib/geoConsentRoutes";

const CrearProducto = lazy(() => import("./pages/dashboard/CrearProducto"));
const SubscripcionesPlanes = lazy(() => import("./pages/dashboard/SubscripcionesPlanes"));
const CloudflareSetup = lazy(() => import("./pages/dashboard/CloudflareSetup"));
const RailwaySetup = lazy(() => import("./pages/dashboard/RailwaySetup"));
const TestBaseTemplate = lazy(() => import("./pages/TestBaseTemplate"));
// Lazy: el chunk `sorprendeme` (manualChunks) se modulepreload-eaba en TODAS
// las páginas por el import estático — en la landing mobile compite con el
// video (replay 019f6c0a…). Solo carga al navegar a /sorprendeme.
const Sorprendeme = lazy(() => import("./pages/Sorprendeme"));
const TestCTAButtons = lazy(() => import("./pages/TestCTAButtons"));
const AnalyticsPreview = import.meta.env.DEV
  ? lazy(() => import("./pages/dev/AnalyticsPreview"))
  : null;

const queryClient = new QueryClient();

/**
 * index.html incluye #seo-static-snapshot para crawlers sin JS. Con React activo el pie real es el de cada
 * plantilla (p. ej. BaseTemplateFooter); si no removemos el snapshot, hay dos footers duplicados.
 */
function RemoveSeoStaticSnapshot() {
  useLayoutEffect(() => {
    document.getElementById("seo-static-snapshot")?.remove();
  }, []);
  return null;
}

// Test routes (test-mux-video, test-mux-country, test-checkout, etc.) solo activas con VITE_ENABLE_TEST_ROUTES=true
const TEST_ROUTES_ENABLED = import.meta.env.VITE_ENABLE_TEST_ROUTES === "true";

// Clerk: VITE_ / NEXT_PUBLIC_ en cliente; build también puede mapear CLERK_PUBLISHABLE_KEY (ver vite.config.ts).
const CLERK_PUBLISHABLE_KEY = getClerkPublishableKey();

if (import.meta.env.DEV && CLERK_PUBLISHABLE_KEY && !isLikelyClerkPublishableKey(CLERK_PUBLISHABLE_KEY)) {
  console.warn(
    "[Clerk] La publishable key no parece válida (esperado pk_test_… o pk_live_…). Revisa variables en .env / Vercel."
  );
}

/**
 * Clerk debe vivir **dentro** de BrowserRouter y recibir routerPush/routerReplace;
 * si no, useAuth puede quedar en isLoaded=false y RedirectToSignIn no muestra login (Clerk v5 + React Router).
 *
 * Perf + aislamiento video (PostHog 019f6c0a… / 019f7a5f… / 019f7a75…): en
 * paths diferidos (`/`, `/invitacion-premium`, `/video-landing`, …) el provider
 * NO se monta — clerk.browser.js no debe competir con el teaser Mux ni spamear
 * errores en IAB. Monta recién al navegar a un path no diferido, donde el
 * remount del subtree coincide con el cambio de ruta (inofensivo).
 *
 * Por qué NO se monta por interacción/idle (hallazgo code review 2026-07-16):
 * flipear clerkOn a mitad de la landing cambia el tipo del child del provider
 * ({children} → <ClerkProvider>{children}</ClerkProvider>) y React remonta TODO
 * el subtree — el teaser Mux se reinicia desde 0 en pleno play, el primer tap
 * se pierde y se duplican eventos. Una vez true, clerkOn nunca vuelve a false
 * (evita el remount inverso al volver a `/`). Los componentes globales con
 * hooks de Clerk leen useClerkGateMounted() (ver src/lib/clerkGate.tsx).
 */
function AppClerkProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const afterAuthUrl = `${pathname}${search}` || "/admin";
  const [clerkOn, setClerkOn] = useState(() => {
    // Geo-sim toolbar: si el operador ya es elegible o hay sim activa, montar
    // Clerk también en paths diferidos (home DualCTA) para mint/exit.
    // eligible vive en localStorage (cruza pestañas: "Ver sitio" abre tab nueva).
    try {
      const eligible =
        localStorage.getItem("admin_geo_sim_eligible") === "1" ||
        sessionStorage.getItem("admin_geo_sim_eligible") === "1";
      const session = sessionStorage.getItem("admin_geo_sim_session");
      const params = new URLSearchParams(window.location.search);
      const geoSimUrl =
        params.get("geo_sim") === "1" || params.get("geo_sim_boot") === "1";
      if (eligible || session || geoSimUrl) return true;
    } catch {
      /* private mode */
    }
    return !isClerkDeferredPath(window.location.pathname);
  });

  useEffect(() => {
    // Solo flipea al navegar a un path NO diferido — nunca por interacción/idle
    // (el flip a mitad de página remonta el subtree y reinicia el video).
    // Excepción: geo-sim elegible/activa (toolbar necesita getToken).
    if (clerkOn) return;
    if (!isClerkDeferredPath(pathname)) {
      setClerkOn(true);
      return;
    }
    try {
      const eligible =
        localStorage.getItem("admin_geo_sim_eligible") === "1" ||
        sessionStorage.getItem("admin_geo_sim_eligible") === "1";
      const session = sessionStorage.getItem("admin_geo_sim_session");
      const boot =
        new URLSearchParams(window.location.search).get("geo_sim_boot") === "1" ||
        new URLSearchParams(window.location.search).get("geo_sim") === "1";
      if (eligible || session || boot) {
        setClerkOn(true);
      }
    } catch {
      /* ignore */
    }
  }, [clerkOn, pathname]);

  if (!CLERK_PUBLISHABLE_KEY || !clerkOn) {
    return (
      <ClerkGateContext.Provider value={false}>
        {children}
      </ClerkGateContext.Provider>
    );
  }

  const authLogoUrl = BRAND_AUTH_LOGO_URL.trim();
  const clerkLayout = {
    socialButtonsVariant: "blockButton" as const,
    ...( /^https?:\/\//i.test(authLogoUrl)
      ? { logoImageUrl: authLogoUrl, logoPlacement: "inside" as const }
      : {} ),
  };

  return (
    <ClerkGateContext.Provider value={true}>
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      routerPush={(to) => {
        void navigate(to);
      }}
      routerReplace={(to) => {
        void navigate(to, { replace: true });
      }}
      signInFallbackRedirectUrl={afterAuthUrl}
      signUpFallbackRedirectUrl={afterAuthUrl}
      localization={getClerkLocale()}
      appearance={{
        baseTheme: dark,
        layout: clerkLayout,
        variables: {
          colorPrimary: "hsl(var(--primary))",
          colorBackground: "hsl(240, 10%, 10%)",
          colorText: "hsl(240, 5%, 95%)",
          colorTextSecondary: "hsl(240, 5%, 72%)",
          colorInputBackground: "hsl(240, 8%, 16%)",
          colorInputText: "hsl(240, 5%, 95%)",
          colorNeutral: "hsl(240, 5%, 95%)",
          borderRadius: "var(--radius)",
        },
      }}
    >
      {children}
    </ClerkProvider>
    </ClerkGateContext.Provider>
  );
}

// Get Clerk locale based on browser language
function getClerkLocale(): typeof esES | typeof enUS {
  if (typeof window !== "undefined") {
    const browserLang = navigator.language || "";
    if (browserLang.startsWith("es")) return esES;
  }
  return enUS;
}

// Loading component while i18n initializes
function I18nLoadingWrapper({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const adminFastPath =
    typeof window !== "undefined" && isAdminShellPath(pathname);
  const [showContent, setShowContent] = useState(adminFastPath);
  const [forceShow, setForceShow] = useState(adminFastPath);

  useEffect(() => {
    if (adminFastPath) {
      setShowContent(true);
      setForceShow(true);
      return;
    }
    // Force show content after 3 seconds even if i18n is not ready
    const forceTimer = setTimeout(() => {
      if (!showContent) {
        console.warn('[I18n] Timeout reached, forcing content display');
        setForceShow(true);
        setShowContent(true);
      }
    }, 3000);

    return () => clearTimeout(forceTimer);
  }, [showContent, adminFastPath]);

  useEffect(() => {
    if (adminFastPath) return;
    // Check if i18n is initialized and language is ready
    if (i18n.isInitialized && i18n.language) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => setShowContent(true), 100);
      return () => clearTimeout(timer);
    }
  }, [i18n.isInitialized, i18n.language, adminFastPath]);

  if (!showContent && !forceShow) {
    // Admin / portal: full black (no white half-screen flash). Public site keeps light splash.
    const darkShell =
      adminFastPath ||
      pathname === "/portal" ||
      pathname.startsWith("/portal/") ||
      pathname.startsWith("/mi-acceso") ||
      pathname.startsWith("/pending");
    // /portal/mi-acceso already covered by /portal/
    return (
      <div
        className={
          darkShell
            ? "fixed inset-0 z-[100] flex min-h-svh flex-col items-center justify-center bg-black"
            : "flex min-h-screen flex-col items-center justify-center bg-white"
        }
      >
        <Loader2
          className={
            darkShell
              ? "mb-4 h-10 w-10 animate-spin text-white/40"
              : "mb-4 h-10 w-10 animate-spin text-black/40"
          }
          aria-hidden
        />
        <p
          className={
            darkShell ? "text-sm text-white/50" : "text-sm text-black/50"
          }
        >
          Cargando...
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

const App = () => {
  if (!CLERK_PUBLISHABLE_KEY) {
    console.error(
      "[Clerk] Falta publishable key. Define VITE_CLERK_PUBLISHABLE_KEY o NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY (o CLERK_PUBLISHABLE_KEY en el build) y redeploy. Sin eso, /admin no puede usar Clerk."
    );
  }

  const appContent = (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <PostHogProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <LanguageProvider>
              <BrowserRouter>
                <GeoConfigSessionRefresh />
                <RegionProvider>
                  <AppClerkProvider>
                  <RemoveSeoStaticSnapshot />
                  <CurrentTemplateProvider>
                    <I18nLoadingWrapper>
                      <LanguageUrlSync />
                      <SkipLink />
                      <PublicSiteShell />
                      <DevilThemeEffect />
                      <BannedGate>
                      <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/minimal" element={<IndexMinimal />} />
                    {/* /test — réplica de la home Argentina (BaseTemplate) con CTA buttons de prueba */}
                    <Route path="/test" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-black/40" /></div>}><TestCTAButtons /></Suspense>} />
                    {/* Rutas de test desactivadas por defecto; solo con VITE_ENABLE_TEST_ROUTES=true */}
                    {TEST_ROUTES_ENABLED ? (
                      <>
                        <Route path="/test-runner" element={<ProtectedTestRoute><TestRunner /></ProtectedTestRoute>} />
                        <Route path="/test-mux-country" element={<ProtectedTestRoute><TestMuxCountry /></ProtectedTestRoute>} />
                        <Route path="/test-mux-video" element={<ProtectedTestRoute><MuxVideoCountryTest /></ProtectedTestRoute>} />
                        <Route path="/test-checkout" element={<ProtectedTestRoute><TestCheckout /></ProtectedTestRoute>} />
                        <Route path="/validate-stripe" element={<ProtectedTestRoute><StripeValidation /></ProtectedTestRoute>} />
                      </>
                    ) : (
                      <>
                        <Route path="/test-runner" element={<Navigate to="/" replace />} />
                        <Route path="/test-mux-country" element={<Navigate to="/" replace />} />
                        <Route path="/test-mux-video" element={<Navigate to="/" replace />} />
                        <Route path="/test-checkout" element={<Navigate to="/" replace />} />
                        <Route path="/validate-stripe" element={<Navigate to="/" replace />} />
                      </>
                    )}
                    {/* Admin shell: sidebar para /admin/*, /dashboard/* y rutas de utilidad */}
                    <Route path="/admin/" element={<Navigate to="/admin" replace />} />
                    {/* /admin/* — layout route con path explícito: elimina el doble-Outlet */}
                    <Route
                      path="admin"
                      element={
                        <ProtectedAdminRoute title="Panel de Administración">
                          <AdminLayout />
                        </ProtectedAdminRoute>
                      }
                    >
                      <Route index element={<AdminDashboard />} />
                      <Route path="analytics" element={<AnalyticsDashboard />} />
                      <Route path="business-pulse" element={<BusinessPulse />} />
                      <Route path="revenue" element={<RevenueAdmin />} />
                      <Route path="recovery" element={<RecoveryAdmin />} />
                      <Route
                        path="campaigns/argentina-win"
                        element={<ArgentinaWinCampaignAdmin />}
                      />
                      <Route path="member-lifecycle-recovery" element={<MemberLifecycleRecovery />} />
                      <Route path="investor-kpi" element={<InvestorKpiAdmin />} />
                      <Route path="access-control" element={<AccessControlAdmin />} />
                      <Route path="finanzas-personales" element={<FinanzasPersonalesAdmin />} />
                      <Route path="site-settings" element={<BaseTemplateAdmin />} />
                      <Route path="invitaciones" element={<GeneradorInvitaciones />} />
                      <Route path="invitaciones/" element={<Navigate to="/admin/invitaciones" replace />} />
                      <Route path="comunidad" element={<ComunidadAdmin />} />
                      <Route path="premium-invite-config" element={<PremiumInviteAdmin />} />
                      <Route path="reels" element={<ReelsAdmin />} />
                      <Route path="reels/upload" element={<ReelsUploadPage />} />
                      <Route path="reels/feed" element={<ReelsFeedPage />} />
                      <Route
                        path="reels/share-links"
                        element={<ReelsShareLinksPage />}
                      />
                      <Route
                        path="reels/secret-share"
                        element={<ReelsSecretSharePage />}
                      />
                      <Route path="portfolio" element={<PortfolioAdmin />} />
                      <Route path="mediakit" element={<MediaKitAdmin />} />
                      <Route path="mediakit/:kitSlug" element={<MediaKitAdmin />} />
                      <Route path="audiovisual" element={<AudiovisualAdmin />} />
                      {/* Editor de CTA por región (provincias AR, ciudades, países).
                          La página se llama internamente `WishlistAdmin` por legacy
                          (la tabla DB sigue siendo wishlist_geo_config) — pero a
                          nivel UX no tenía coherencia con lo que controla. La
                          ruta canónica ahora es `/admin/cta-por-region`. La ruta
                          vieja `/admin/wishlist-config` queda como redirect para
                          que links externos y bookmarks no se rompan. */}
                      <Route path="cta-por-region" element={<WishlistAdmin />} />
                      <Route
                        path="wishlist-config"
                        element={<Navigate to="/admin/cta-por-region" replace />}
                      />
                      <Route
                        path="hero-cta"
                        element={<Navigate to="/admin/cta-por-region" replace />}
                      />
                      <Route path="country-template" element={<CountryTemplateAdmin />} />
                      <Route path="geo-traffic" element={<GeoTrafficDashboard />} />
                      <Route
                        path="visitor-experience"
                        element={<VisitorExperienceAdmin />}
                      />
                      <Route
                        path="visitor-experience/funnels"
                        element={<VisitorExperienceFunnelsAdmin />}
                      />
                      <Route path="fotos-analytics" element={<PhotoAnalyticsAdmin />} />
                      <Route path="web-templates" element={<WebTemplatesAnalyticsAdmin />} />
                      <Route path="mux-analytics" element={<MuxAnalyticsAdmin />} />
                      <Route path="mux-conversion" element={<MuxConversionAdmin />} />
                      <Route path="conversion-health" element={<ConversionHealthAdmin />} />
                      <Route path="iplocate-health" element={<IpLocateHealthAdmin />} />
                      <Route path="worker-flags" element={<WorkerFlagsAdmin />} />
                      <Route path="config-health" element={<ConfigHealthAdmin />} />
                      <Route path="notifications-health" element={<NotificationsHealth />} />
                      <Route path="active-members" element={<ActiveMembersAdmin />} />
                      <Route path="email-broadcast" element={<EmailBroadcast />} />
                      <Route path="audit-log" element={<AdminAuditLog />} />
                      <Route path="memberships" element={<MembershipApprovals />} />
                      <Route path="stripe-disputes" element={<StripeDisputesAdmin />} />
                      <Route path="stripe-payments-config" element={<StripePaymentsConfigAdmin />} />
                      <Route path="sorprendeme-config" element={<SorprendemeConfigAdmin />} />
                      <Route path="whatsapp-broadcast-config" element={<WhatsAppBroadcastAdmin />} />
                      <Route path="dual-cta-home" element={<DualCtaHomeAdmin />} />
                      <Route path="dual-cta-templates" element={<DualCtaVideoTemplatesAdmin />} />
                      <Route path="mercadopago-plans" element={<MercadoPagoPlansAdmin />} />
                      <Route
                        path="video-ab-slot"
                        element={<Navigate to="/admin/dual-cta-home" replace />}
                      />
                      {/* Legacy route kept only as alias to avoid dead links/404s. */}
                      <Route
                        path="video-ab-slots"
                        element={<Navigate to="/admin/dual-cta-home" replace />}
                      />
                      <Route path="free-members" element={<FreeMemberApprovals />} />
                      <Route path="*" element={<Navigate to="/admin" replace />} />
                    </Route>
                    {/* /dashboard/* — mismo shell */}
                    <Route
                      path="dashboard"
                      element={
                        <ProtectedAdminRoute title="Panel de Administración">
                          <AdminLayout />
                        </ProtectedAdminRoute>
                      }
                    >
                      <Route path="crear-producto" element={<CrearProducto />} />
                      <Route path="subscripciones/planes" element={<SubscripcionesPlanes />} />
                      <Route path="railway-setup" element={<RailwaySetup />} />
                      <Route path="cloudflare-setup" element={<CloudflareSetup />} />
                      <Route path="vercel-setup" element={<Navigate to="/dashboard/railway-setup" replace />} />
                      <Route path="setup" element={<Navigate to="/dashboard/railway-setup" replace />} />
                      <Route path="*" element={<Navigate to="/dashboard/railway-setup" replace />} />
                    </Route>
                    {/* Rutas de utilidad con el admin shell (sin prefijo /admin) */}
                    <Route
                      element={
                        <ProtectedAdminRoute title="Panel de Administración">
                          <AdminLayout />
                        </ProtectedAdminRoute>
                      }
                    >
                      <Route path="test-base-template" element={<TestBaseTemplate />} />
                      <Route path="test-cta-buttons" element={<TestCTAButtons />} />
                    </Route>
                    <Route path="/gift/:slug" element={<GiftProduct />} />
                    <Route path="/p/mock-usd" element={<ProductPageMockUsd />} />
                    <Route path="/p/mock-ars" element={<ProductPageMockArs />} />
                    <Route path="/p/:slug" element={<ProductPageSimple />} />
                    <Route path="/buy/:slug" element={<DirectCheckout />} />
                    <Route path="/reel/:slug" element={<ProductReel />} />
                    <Route path="/test/reel/:slug" element={<ProductReelMockTest />} />
                    <Route
                      path="/canal"
                      element={<ChannelInviteLanding />}
                    />
                    <Route
                      path="/channel"
                      element={<ChannelInviteLanding />}
                    />
                    <Route
                      path="/checkout-channel-invite-router"
                      element={<CheckoutChannelInviteRouter />}
                    />
                    <Route
                      path="/checkout-channel-invite"
                      element={<CheckoutPage />}
                    />
                    <Route
                      path="/checkout-ar-channel-invite"
                      element={<CheckoutArChannelInvite />}
                    />
                    <Route path="/pagar/:paymentIntentId" element={<PagarPage />} />
                    <Route path="/generator" element={<InviteGenerator />} />
                    <Route path="/invitacion" element={<PremiumInvite />} />
                    <Route
                      path="/invitacion-mejores-amigos"
                      element={<InvitacionGrupo />}
                    />
                    <Route
                      path="/grupo-privado"
                      element={<InvitacionGrupo />}
                    />
                    <Route path="/invite" element={<Invite />} />
                    <Route
                      path="/creadora-de-contenido"
                      element={<CreadoraContenido />}
                    />
                    <Route
                      path="/content-creator"
                      element={<ContentCreator />}
                    />
                    <Route path="/influencer" element={<Influencer />} />
                    <Route path="/fotos" element={<Fotos />} />
                    <Route path="/imagenes" element={<Imagenes />} />
                    <Route path="/reels/:token" element={<ReelInviteViewer />} />
                    <Route path="/reels" element={<Reels />} />
                    <Route path="/videos" element={<Videos />} />
                    <Route path="/video" element={<Video />} />
                    <Route path="/lifestyle" element={<Lifestyle />} />
                    <Route path="/nutricion" element={<Nutricion />} />
                    <Route path="/belleza" element={<Belleza />} />
                    <Route path="/viajes" element={<Viajes />} />
                    <Route path="/tiktok" element={<TikTok />} />
                    <Route path="/instagram" element={<Instagram />} />
                    <Route path="/youtube" element={<YouTube />} />
                    <Route path="/erome" element={<Erome />} />
                    <Route path="/filtrados" element={<Filtrado />} />
                    <Route path="/filtro" element={<Filtro />} />
                    <Route path="/leaked" element={<Leaked />} />
                    <Route path="/onlyfans" element={<Onlyfans />} />
                    <Route path="/pack" element={<Pack />} />
                    <Route path="/pack1" element={<Pack1 />} />
                    <Route path="/pack2" element={<Pack2 />} />
                    <Route path="/pack3" element={<Pack3 />} />
                    <Route path="/desnuda" element={<Desnuda />} />
                    <Route path="/nude" element={<Nude />} />
                    <Route path="/premium" element={<Premium />} />
                    <Route path="/privado" element={<Privado />} />
                    <Route path="/contenido" element={<Contenido />} />
                    <Route path="/exclusivo" element={<Exclusivo />} />
                    <Route path="/gratis" element={<Gratis />} />
                    <Route path="/galeria" element={<Galeria />} />
                    <Route path="/fotos-desnuda" element={<FotosDesnuda />} />
                    <Route path="/videos-desnuda" element={<VideosDesnuda />} />
                    <Route path="/pornhub" element={<Pornhub />} />
                    <Route path="/xvideos" element={<Xvideos />} />
                    <Route path="/fotos-gratis" element={<FotosGratis />} />
                    <Route path="/videos-gratis" element={<VideosGratis />} />
                    <Route path="/tiktoker" element={<Tiktoker />} />
                    <Route path="/modelo" element={<Modelo />} />
                    <Route path="/photography" element={<Photography />} />
                    <Route path="/artista" element={<Artista />} />
                    <Route path="/fashion" element={<Fashion />} />
                    <Route path="/wellness" element={<Wellness />} />
                    {/* 301 hub: server also redirects; keep client Navigate for SPA nav */}
                    <Route
                      path="/digital-creator"
                      element={<Navigate to="/content-creator" replace />}
                    />
                    <Route path="/social-media" element={<SocialMedia />} />
                    <Route path="/personalidad" element={<Personalidad />} />
                    <Route path="/beauty-expert" element={<BeautyExpert />} />
                    <Route
                      path="/travel-influencer"
                      element={<TravelInfluencer />}
                    />
                    <Route path="/creative" element={<Creative />} />
                    <Route path="/argentina" element={<Argentina />} />
                    <Route path="/ar/invitacion" element={<InvitacionExclusivaArgentina />} />
                    <Route path="/ar/invite" element={<InvitacionExclusivaArgentina />} />
                    {/* Checkout AR result pages - redirect a Telegram */}
                    <Route path="/ar/invitacion/success" element={<InvitacionArSuccess />} />
                    <Route path="/ar/invitacion/error" element={<InvitacionArError />} />
                    <Route path="/ar/invitacion/pending" element={<InvitacionArPending />} />

                    {/* Flujo VIP: registro → perfil → pending → /portal/mi-acceso (link asignado) */}
                    <Route path={MEMBERSHIP_ROUTE.REGISTRO} element={<Registro mode="sign-up" />} />
                    <Route path={MEMBERSHIP_ROUTE.PORTAL_REGISTRO} element={<Registro mode="sign-up" />} />
                    <Route path={MEMBERSHIP_ROUTE.ENTRAR} element={<Registro mode="sign-in" />} />
                    <Route path={MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO} element={<CompleteRegistration />} />
                    <Route path={MEMBERSHIP_ROUTE.PORTAL_COMPLETAR_REGISTRO} element={<CompleteRegistration />} />
                    <Route path={MEMBERSHIP_ROUTE.PENDING_APPROVAL} element={<PendingApproval />} />
                    <Route path={MEMBERSHIP_ROUTE.MI_ACCESO} element={<MiAcceso />} />
                    {/* Legacy: /mi-acceso → canónico bajo portal */}
                    <Route
                      path={MEMBERSHIP_ROUTE.MI_ACCESO_LEGACY}
                      element={<Navigate to={MEMBERSHIP_ROUTE.MI_ACCESO} replace />}
                    />
                    {/* Portal del rol "user": logueado sin rol admin → pendiente de asignación */}
                    <Route path="/portal" element={<UserPortal />} />
                    <Route path={MEMBERSHIP_ACCESS_PORTAL_PATH} element={<MembershipAccessPortal />} />
                    {/* BioTemplatePremium — invitación global por país (ARS=MercadoPago / USD=Stripe) */}
                    <Route path="/invitacion-premium" element={<PremiumInvite />} />
                    
                    {/* Legacy: /membresia-vip redirige a /invitacion-premium */}
                    <Route path="/membresia-vip" element={<MembresiaPremiumVip />} />
                    
                    {/* Test pages para desarrolladores — sin restricciones */}
                    <Route path="/test-new-template" element={<TestNewTemplate />} />
                    <Route path="/test-premium-argentina" element={<TestPremiumArgentina />} />
                    <Route path="/test-premium-internacional" element={<TestPremiumInternacional />} />
                    <Route path="/premium-invite" element={<PremiumInvite />} />
                    <Route path="/muchas-gracias" element={<PremiumInviteSuccess />} />
                    <Route path="/invitacion-premium/success" element={<PremiumInviteSuccess />} />
                    <Route path="/buenos-aires" element={<BuenosAires />} />
                    {/* Colombia + English alternatives — BaseTemplate rich pages with Stripe (admin control like AR provinces via country-template + heroCta + site-settings) */}
                    <Route path="/colombia" element={<Colombia />} />
                    <Route path="/en/colombia" element={<Colombia />} />
                    <Route path="/medellin" element={<Medellin />} />
                    <Route path="/en/medellin" element={<Medellin />} />
                    <Route path="/bogota" element={<Bogota />} />
                    <Route path="/en/bogota" element={<Bogota />} />
                    <Route path="/cali" element={<Cali />} />
                    <Route path="/en/cali" element={<Cali />} />
                    <Route path="/cartagena" element={<Cartagena />} />
                    <Route path="/en/cartagena" element={<Cartagena />} />
                    <Route path="/mexico" element={<Mexico />} />
                    <Route path="/guadalajara" element={<Guadalajara />} />
                    <Route path="/ciudad-de-mexico" element={<CiudadDeMexico />} />
                    <Route path="/chile" element={<Chile />} />
                    <Route path="/santiago" element={<Santiago />} />
                    <Route path="/espana" element={<Espana />} />
                    <Route path="/madrid" element={<Madrid />} />
                    <Route path="/barcelona" element={<Barcelona />} />
                    <Route path="/deportista" element={<Deportista />} />
                    <Route path="/fitness" element={<Fitness />} />
                    <Route path="/entrenamiento" element={<Entrenamiento />} />
                    <Route path="/atleta" element={<Atleta />} />
                    <Route path="/sobre-mi" element={<SobreMi />} />
                    <Route path="/biografia" element={<Biografia />} />
                    <Route path="/contacto" element={<Contacto />} />
                    <Route path="/latina-influencer" element={<LatinaInfluencer />} />
                    <Route path="/hispanic-influencer" element={<HispanicInfluencer />} />
                    <Route
                      path="/latin-influencer"
                      element={<Navigate to="/latina-influencer" replace />}
                    />
                    <Route path="/modella-argentina" element={<ModellaArgentina />} />
                    <Route path="/italia" element={<Italia />} />
                    <Route path="/london" element={<London />} />
                    <Route path="/usa" element={<Usa />} />
                    <Route path="/miami" element={<Miami />} />
                    <Route path="/new-york" element={<NewYork />} />
                    <Route path="/los-angeles" element={<LosAngeles />} />
                    <Route path="/rutina" element={<Rutina />} />
                    <Route path="/recetas" element={<Recetas />} />
                    <Route path="/skincare" element={<Skincare />} />
                    <Route
                      path="/gracias-por-tu-compra"
                      element={<GraciasPorTuCompra />}
                    />
                    <Route
                      path="/gracias-por-tu-compra-latam"
                      element={<GraciasPorTuCompraLatam />}
                    />
                    {/* Thank you pages: required for Stripe Elements custom → validate purchase & Telegram entry button */}
                    <Route path="/gracias" element={<Gracias />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                    <Route path="/thank-you-a-lot" element={<ThankYouALot />} />
                    {/* Regalo Lucas page */}
                    {/* <Route path="/regalo-lucas" element={<RegaloLucas />} /> oculto mientras se trabaja */}
                    <Route
                      path="/membresia-telegram"
                      element={<MembresiaTelegram />}
                    />
                    <Route
                      path="/invitacion-privada"
                      element={<FullscreenExclusive />}
                    />
                    <Route
                      path="/video-landing"
                      element={<DualCTAVideoLanding />}
                    />
                    <Route
                      path="/exclusive"
                      element={<FullscreenExclusive />}
                    />
                    <Route
                      path="/exclusive-ar"
                      element={<FullscreenExclusiveAr />}
                    />
                    <Route path="/unete" element={<InviteMembership />} />
                    <Route
                      path="/exclusive-ar/success"
                      element={<ExclusivoArSuccess />}
                    />
                    {/* Alias ES del success VIP AR (back_url legacy /exclusivo-ar/success) */}
                    <Route
                      path="/exclusivo-ar/success"
                      element={<ExclusivoArSuccess />}
                    />
                    <Route
                      path="/exclusivo-ar"
                      element={<FullscreenExclusiveAr />}
                    />
                    <Route
                      path="/sorpresa"
                      element={<FullscreenSorpresa />}
                    />
                    <Route
                      path="/sorprendeme"
                      element={
                        <Suspense fallback={null}>
                          <Sorprendeme />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/r/cumple"
                      element={<RegaloCumple />}
                    />
                    <Route
                      path="/r/membresia-video"
                      element={<MembershipRecoveryVideo />}
                    />
                    <Route
                      path="/vip/:nombre"
                      element={<VipCustomPayment />}
                    />
                    <Route
                      path="/test-exclusivo"
                      element={<TestExclusivo />}
                    />
                    <Route
                      path="/whatsapp-broadcast"
                      element={<WhatsAppBroadcast />}
                    />
                    <Route
                      path="/terminos-y-condiciones"
                      element={<TerminosYCondiciones />}
                    />
                    <Route
                      path="/dar-de-baja"
                      element={<DarDeBaja />}
                    />
                    <Route
                      path="/portfolio"
                      element={<Portfolio />}
                    />
                    <Route
                      path="/mediakit-intro"
                      element={<Navigate to="/mediakit" replace />}
                    />
                    {/* Public commercial media kits — general + verticals by rubro */}
                    <Route path="/mediakit" element={<MediaKitPage />} />
                    <Route path="/mediakit/:slug" element={<MediaKitPage />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    {import.meta.env.DEV && AnalyticsPreview ? (
                      <Route
                        path="/dev/analytics-preview"
                        element={
                          <Suspense fallback={null}>
                            <AnalyticsPreview />
                          </Suspense>
                        }
                      />
                    ) : null}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                      </BannedGate>
                    </I18nLoadingWrapper>
                </CurrentTemplateProvider>
                  </AppClerkProvider>
                </RegionProvider>
              </BrowserRouter>
            </LanguageProvider>
          </TooltipProvider>
      </PostHogProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );

  return appContent;
};

export default App;
